import { useEffect, useMemo, useState } from 'react'
import {
  createParser,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
  useQueryStates,
} from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { toast } from 'sonner'
import { AP_INVOICE_REVIEW_QUEUES } from '../constants'
import type { ApInvoiceReviewQueueKey } from '../types'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'

const PAGE_SIZE = 20
export type AssigneeFilterValue = 'all' | 'unassigned' | `staff:${number}`
export type LinkFilterValue =
  'all' | 'po_linked' | 'shipping_linked' | 'unlinked' | 'project_linked'
export type IntakeSourceFilterValue = 'all' | 'manual' | 'mailbox' | 'tracker'

const REVIEW_QUEUES = ['all', ...AP_INVOICE_REVIEW_QUEUES.map((queue) => queue.key)] as const
const ARCHIVE_VIEWS = ['active', 'archived', 'all'] as const
const LINK_FILTERS = [
  'all',
  'po_linked',
  'shipping_linked',
  'unlinked',
  'project_linked',
] as const satisfies readonly LinkFilterValue[]
const INTAKE_SOURCES = [
  'all',
  'manual',
  'mailbox',
  'tracker',
] as const satisfies readonly IntakeSourceFilterValue[]
const parseAsAssignee = createParser<AssigneeFilterValue>({
  parse: (value) =>
    value === 'all' || value === 'unassigned' || /^staff:\d+$/.test(value)
      ? (value as AssigneeFilterValue)
      : null,
  serialize: (value) => value,
})
const SORT_DIRS = ['asc', 'desc'] as const

interface InvoiceViewSnapshot {
  search: string
  status: string
  priority: string
  reviewQueue: ApInvoiceReviewQueueKey | 'all'
  archiveView: 'active' | 'archived' | 'all'
  linkType: LinkFilterValue
  intakeSource: IntakeSourceFilterValue
  assigneeFilter: AssigneeFilterValue
  dueFrom: string
  dueTo: string
  sort: { by: string; dir: 'asc' | 'desc' }
}

interface InvoiceSavedView {
  id: string
  name: string
  filters: InvoiceViewSnapshot
}

const SAVED_VIEWS_STORAGE_KEY = 'usars-ap-invoice-saved-views-v1'

function loadSavedViews(): InvoiceSavedView[] {
  if (typeof window === 'undefined') return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(SAVED_VIEWS_STORAGE_KEY) || '[]')
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (view): view is InvoiceSavedView =>
          Boolean(view) &&
          typeof view.id === 'string' &&
          typeof view.name === 'string' &&
          typeof view.filters === 'object',
      )
      .map((view) => ({
        ...view,
        filters: {
          ...view.filters,
          linkType:
            (view.filters.linkType as string) === 'non_po'
              ? 'shipping_linked'
              : (view.filters.linkType as string) === 'no_link'
                ? 'unlinked'
                : view.filters.linkType,
        },
      }))
  } catch {
    return []
  }
}

export function useInvoiceListFilters() {
  // Filters live in the URL so Back from an invoice restores the filtered list (USA-1358).
  const [search, setSearch] = useUrlSearchText()
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1))
  const [status, setStatus] = useQueryState('status', parseAsString.withDefault('all'))
  const [priority, setPriority] = useQueryState('priority', parseAsString.withDefault('all'))
  const [reviewQueue, setReviewQueue] = useQueryState(
    'queue',
    parseAsStringLiteral(REVIEW_QUEUES).withDefault('what_needs_paying'),
  )
  const [archiveView, setArchiveView] = useQueryState(
    'archive',
    parseAsStringLiteral(ARCHIVE_VIEWS).withDefault('active'),
  )
  const [linkType, setLinkType] = useQueryState(
    'link',
    parseAsStringLiteral(LINK_FILTERS).withDefault('project_linked'),
  )
  const [intakeSource, setIntakeSource] = useQueryState(
    'source',
    parseAsStringLiteral(INTAKE_SOURCES).withDefault('all'),
  )
  const [dueFrom, setDueFrom] = useQueryState('due_from', parseAsString.withDefault(''))
  const [dueTo, setDueTo] = useQueryState('due_to', parseAsString.withDefault(''))
  const [assigneeFilter, setAssigneeFilter] = useQueryState(
    'assignee',
    parseAsAssignee.withDefault('all'),
  )
  const [savedViews, setSavedViews] = useState<InvoiceSavedView[]>(loadSavedViews)
  const [selectedSavedViewId, setSelectedSavedViewId] = useState('none')
  const [savedViewName, setSavedViewName] = useState('')
  const [sort, setSort] = useQueryStates(
    {
      by: parseAsString.withDefault('contractual_urgency'),
      dir: parseAsStringLiteral(SORT_DIRS).withDefault('asc'),
    },
    { urlKeys: { by: 'sort', dir: 'dir' } },
  )
  const { staffId } = useMyProjectsFilter()
  const selectedAssigneeId = assigneeFilter.startsWith('staff:')
    ? Number(assigneeFilter.replace('staff:', ''))
    : undefined

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.localStorage?.setItem === 'function') {
      window.localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(savedViews))
    }
  }, [savedViews])

  const params = useMemo(
    () => ({
      search: search || undefined,
      status: status === 'all' ? undefined : status,
      priority: priority === 'all' ? undefined : priority,
      review_queue: reviewQueue === 'all' ? undefined : reviewQueue,
      assigned_user_id: staffId ?? selectedAssigneeId ?? undefined,
      unassigned: staffId ? undefined : assigneeFilter === 'unassigned' ? true : undefined,
      archived: archiveView === 'all' ? undefined : archiveView === 'archived',
      include_archived: archiveView === 'all' ? true : undefined,
      link_type: linkType === 'all' ? undefined : linkType,
      intake_source: intakeSource === 'all' ? undefined : intakeSource,
      due_from: dueFrom || undefined,
      due_to: dueTo || undefined,
      page,
      limit: PAGE_SIZE,
      sort_by: sort.by,
      sort_dir: sort.dir,
    }),
    [
      archiveView,
      assigneeFilter,
      dueFrom,
      dueTo,
      intakeSource,
      linkType,
      page,
      priority,
      reviewQueue,
      search,
      selectedAssigneeId,
      sort.by,
      sort.dir,
      staffId,
      status,
    ],
  )

  const toggleSort = (by: string) => {
    setSort((current) => ({
      by,
      dir: current.by === by && current.dir === 'asc' ? 'desc' : 'asc',
    }))
  }

  const resetPage = () => setPage(1)

  // Choosing a queue picks its own link filter, so that pairing is not counted as a filter set.
  const queueLinkType: LinkFilterValue =
    reviewQueue === 'what_needs_paying'
      ? 'project_linked'
      : reviewQueue === 'unlinked_po_project'
        ? 'unlinked'
        : 'all'

  const activeFilterCount = [
    reviewQueue !== 'what_needs_paying',
    assigneeFilter !== 'all',
    status !== 'all',
    priority !== 'all',
    linkType !== queueLinkType,
    intakeSource !== 'all',
    archiveView !== 'active',
    Boolean(dueFrom),
    Boolean(dueTo),
  ].filter(Boolean).length

  const resetFilters = () => {
    setSearch('')
    setStatus('all')
    setPriority('all')
    setReviewQueue('what_needs_paying')
    setArchiveView('active')
    setLinkType('project_linked')
    setIntakeSource('all')
    setAssigneeFilter('all')
    setDueFrom('')
    setDueTo('')
    setSort({ by: 'contractual_urgency', dir: 'asc' })
    setSelectedSavedViewId('none')
    setSavedViewName('')
    resetPage()
  }

  const currentViewSnapshot = (): InvoiceViewSnapshot => ({
    search,
    status,
    priority,
    reviewQueue,
    archiveView,
    linkType,
    intakeSource,
    assigneeFilter,
    dueFrom,
    dueTo,
    sort,
  })

  const applySavedView = (view: InvoiceSavedView) => {
    const filters = view.filters
    setSearch(filters.search || '')
    setStatus(filters.status || 'all')
    setPriority(filters.priority || 'all')
    setReviewQueue(filters.reviewQueue || 'what_needs_paying')
    setArchiveView(filters.archiveView || 'active')
    setLinkType(filters.linkType || 'all')
    setIntakeSource(filters.intakeSource || 'all')
    setAssigneeFilter(filters.assigneeFilter || 'all')
    setDueFrom(filters.dueFrom || '')
    setDueTo(filters.dueTo || '')
    setSort(filters.sort || { by: 'contractual_urgency', dir: 'asc' })
    setSelectedSavedViewId(view.id)
    setSavedViewName(view.name)
    resetPage()
  }

  const saveCurrentView = () => {
    const name = savedViewName.trim()
    if (!name) {
      toast.error('Name this AP view before saving it')
      return
    }
    const existing = savedViews.find(
      (view) =>
        view.id === selectedSavedViewId ||
        view.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
    )
    const view: InvoiceSavedView = {
      id: existing?.id || `ap-view-${Date.now()}`,
      name,
      filters: currentViewSnapshot(),
    }
    setSavedViews((current) => [...current.filter((item) => item.id !== view.id), view])
    setSelectedSavedViewId(view.id)
    toast.success(existing ? 'Saved AP view updated' : 'AP view saved')
  }

  const deleteSelectedView = () => {
    if (selectedSavedViewId === 'none') return
    setSavedViews((current) => current.filter((view) => view.id !== selectedSavedViewId))
    setSelectedSavedViewId('none')
    setSavedViewName('')
    toast.success('Saved AP view removed')
  }

  return {
    params,
    page,
    setPage,
    toggleSort,
    search,
    setSearch,
    status,
    setStatus,
    priority,
    setPriority,
    reviewQueue,
    setReviewQueue,
    archiveView,
    setArchiveView,
    linkType,
    setLinkType,
    intakeSource,
    setIntakeSource,
    dueFrom,
    setDueFrom,
    dueTo,
    setDueTo,
    assigneeFilter,
    setAssigneeFilter,
    savedViews,
    selectedSavedViewId,
    setSelectedSavedViewId,
    savedViewName,
    setSavedViewName,
    setSort,
    staffId,
    resetPage,
    activeFilterCount,
    resetFilters,
    applySavedView,
    saveCurrentView,
    deleteSelectedView,
  }
}
